import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import RoyalCollectOverlaySymbolController from "./RoyalCollectOverlaySymbolController";
import { safeInit } from "../../utilities";

/**
 * RoyalCollectOverlaySymbol
 *
 * Container for spine animations in Royal Collect feature.
 * Each symbol position has its own controller that manages ONE coin spine.
 * Mirrors HoldAndWinOverlaySymbol pattern (without multipliers).
 */
const RoyalCollectOverlaySymbol = forwardRef(
  ({ index, reelIndex, y, symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);

    const containerName = useMemo(
      () => `rc_overlay_symbol_${reelIndex}_${index}`,
      [reelIndex, index],
    );

    // Initialize controller once
    useEffect(() => {
      if (!controllerRef.current && containerRef.current) {
        safeInit(() => {
          controllerRef.current = new RoyalCollectOverlaySymbolController(
            containerRef.current,
            reelIndex,
            index,
            symbolWidth,
            symbolHeight,
          );
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle resize
    useEffect(() => {
      if (controllerRef.current) {
        controllerRef.current.resizeAnimations(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    // Memoize exposed methods
    const getController = useCallback(() => controllerRef.current, []);

    const showLandingAnimation = useCallback((symbolId) => {
      controllerRef.current?.showLandingAnimation(symbolId);
    }, []);

    const showIdleAnimation = useCallback((symbolId) => {
      controllerRef.current?.showIdleAnimation(symbolId);
    }, []);

    const showWinAnimation = useCallback(
      (symbolId, loop = true, showText = true) => {
        controllerRef.current?.showWinAnimation(symbolId, loop, showText);
      },
      [],
    );

    const showCollectAnimation = useCallback(
      (symbolId, showText = true) => {
        controllerRef.current?.showCollectAnimation(symbolId, showText);
      },
      [],
    );

    const hideAnimation = useCallback(() => {
      controllerRef.current?.hideAnimation();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getController,
        showLandingAnimation,
        showIdleAnimation,
        showWinAnimation,
        showCollectAnimation,
        hideAnimation,
      }),
      [
        getController,
        showLandingAnimation,
        showIdleAnimation,
        showWinAnimation,
        showCollectAnimation,
        hideAnimation,
      ],
    );

    return (
      <Container
        ref={containerRef}
        y={y}
        x={0}
        zIndex={7}
        sortableChildren
        name={containerName}
      />
    );
  },
);

RoyalCollectOverlaySymbol.displayName = "RoyalCollectOverlaySymbol";

export default RoyalCollectOverlaySymbol;
